import request from "@common/request";
import { useMutation, useQueryClient } from "react-query";
import { MutateTaskContext, MutateTaskState, RemoveTaskContext } from "./types";
import { keyBy, random } from "lodash";
import moment from "moment";
import { TaskStates, TaskType } from "./constants";
import { useMutationFormeBackendApi } from "@modules/forme/queries";
import {
  DisabilityInsurancePayload,
  ProfilePayload,
  RiskTolerancePayload,
  TLPayload,
} from "@earned/wizard";

const useMutateTaskState = () => {
  const queryClient = useQueryClient();
  return useMutation(
    "changeTaskState",
    ({ taskId, state, animationDuration = 0 }: MutateTaskState) =>
      request<Forme.EnrichedTask>("POST", `/api/tasks/${taskId}/task-states`, {
        state,
        animationDuration,
      }),
    {
      onMutate: async (mutatedTaskState) => {
        await queryClient.cancelQueries({
          queryKey: ["taskList"],
        });
        const previousTaskList = queryClient.getQueryData<Forme.EnrichedTask[]>(
          ["taskList"]
        );
        const previousTaskListById = keyBy(previousTaskList, "id");

        // Time out must match the animation duration
        setTimeout(() => {
          queryClient.setQueryData<Forme.EnrichedTask[]>(
            ["taskList"],
            Object.values({
              ...previousTaskListById,
              [mutatedTaskState.taskId]: {
                ...previousTaskListById[mutatedTaskState.taskId],
                taskStates: [
                  ...(previousTaskListById[mutatedTaskState.taskId]
                    ?.taskStates || []),
                  {
                    state: mutatedTaskState.state,
                    id: random(100000).toString(),
                    createdAt: moment().add(1, "day").format(),
                  },
                ],
              },
            }) as Forme.EnrichedTask[]
          );
        }, mutatedTaskState.animationDuration);

        return { previousTaskList };
      },
      onError: (err, mutatedTaskState, context) => {
        const typedContext: MutateTaskContext =
          context as unknown as MutateTaskContext;
        queryClient.setQueryData<Forme.EnrichedTask[]>(
          ["taskList"],
          typedContext.previousMutatedTasksState
        );
      },
      onSettled: (mutatedTaskState) => {
        if (mutatedTaskState)
          queryClient.invalidateQueries<Forme.EnrichedTask[]>({
            queryKey: ["taskList"],
          });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["getOpenTasksCount"],
        });
      },
    }
  );
};

const useDeleteTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    "removeTask",
    ({ id }: { id: string }) =>
      request<Forme.Task>("DELETE", "/api/tasks", {
        id,
      }),
    {
      onMutate: async (params) => {
        await queryClient.cancelQueries({
          queryKey: ["taskList"],
        });
        const previousTasks = queryClient.getQueryData<Forme.EnrichedTask[]>([
          "taskList",
        ]);

        const taskToRemove = previousTasks?.find(
          (task) => task.id === params.id
        );

        if (taskToRemove) taskToRemove.active = false;

        queryClient.setQueryData<Forme.EnrichedTask[]>(
          ["taskList"],
          [
            ...(previousTasks as Forme.EnrichedTask[]),
            taskToRemove as Forme.EnrichedTask,
          ]
        );

        return { previousTasks };
      },
      onError: (err, taskToRemove, context) => {
        const typedContext: RemoveTaskContext =
          context as unknown as RemoveTaskContext;
        queryClient.setQueryData(["taskList"], typedContext.previousTasks);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["taskList"],
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["getOpenTasksCount"],
        });
      },
    }
  );
};
const useEditTaskAttachedNote = () => {
  return useMutation(
    "editTaskAttachedNote",
    (task: { id: string; attachedNote?: string | null }) =>
      request<Forme.Task>("PUT", `/api/tasks/${task.id}/attached-note`, {
        attachedNote: task.attachedNote || null,
      })
  );
};

const useEditTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    "editTask",
    (task: {
      id: string;
      state?: TaskStates;
      name?: string;
      description?: string;
      productId?: string | null;
      estimatedCompletionTime?: number | null;
      callToAction?: string | null;
      productTopic?: string | null;
      productPhase?: string | null;
      templateId?: string | null;
      completeWithAdvisor?: boolean;
      attachedNote?: string | null;
      downloadablePdfUrl?: string | null;
      sortIndexTaskIdAbove?: string;
      sortIndexTaskIdBelow?: string;
      ctaUrl?: string | null;
      ctaText?: string | null;
      dueDate?: string | null;
    }) =>
      request<Forme.Task>("PUT", "/api/tasks", {
        id: task.id,
        state: task.state || null,
        name: task.name || null,
        description: task.description || null,
        productId: task.productId || null,
        estimatedCompletionTime: task.estimatedCompletionTime || null,
        callToAction: task.callToAction || null,
        productTopic: task.productTopic || null,
        productPhase: task.productPhase || null,
        templateId: task.templateId || null,
        completeWithAdvisor: task.completeWithAdvisor || false,
        attachedNote: task.attachedNote || null,
        downloadablePdfUrl: task.downloadablePdfUrl || null,
        sortIndexTaskIdAbove: task.sortIndexTaskIdAbove || null,
        sortIndexTaskIdBelow: task.sortIndexTaskIdBelow || null,
        ctaUrl: task.ctaUrl || null,
        ctaText: task.ctaText || null,
        dueDate: task.dueDate || null,
      }),
    {
      onMutate: async (taskToEdit) => {
        await queryClient.cancelQueries({
          queryKey: ["taskList"],
        });
        const previousTasks = queryClient.getQueryData<Forme.EnrichedTask[]>([
          "taskList",
        ]);

        const previousTaskListById = keyBy(previousTasks, "id");

        queryClient.setQueryData<Forme.EnrichedTask[]>(
          ["taskList"],
          Object.values({
            ...previousTaskListById,
            [taskToEdit.id]: {
              ...previousTaskListById[taskToEdit.id],
              name: taskToEdit.name,
              description: taskToEdit.description,
              productId: taskToEdit.productId,
              estimatedCompletionTime: taskToEdit.estimatedCompletionTime,
              callToAction: taskToEdit.callToAction,
              productTopic: taskToEdit.productTopic,
              productPhase: taskToEdit.productPhase,
              templateId: taskToEdit.templateId,
              completeWithAdvisor: taskToEdit.completeWithAdvisor,
              attachedNote: taskToEdit.attachedNote,
              taskStates: [...previousTaskListById[taskToEdit.id].taskStates],
              dueDate: taskToEdit.dueDate,
            },
          }) as Forme.EnrichedTask[]
        );

        return { previousTasks };
      },
      onError: (err, taskToRemove, context) => {
        const typedContext: RemoveTaskContext =
          context as unknown as RemoveTaskContext;
        queryClient.setQueryData(["taskList"], typedContext.previousTasks);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["taskList"],
        });
      },
    }
  );
};

const useCreateTask = () => {
  const queryClient = useQueryClient();
  return useMutation(
    "addTask",
    (task: {
      name: string;
      description?: string;
      productTopic?: string;
      callToAction?: string;
      estimatedCompletionTime?: number;
      productId?: string;
      productPhase?: string;
      templateId?: string;
      completeWithAdvisor?: boolean;
      downloadablePdfUrl?: string;
      triggerNotification?: boolean;
      ctaUrl?: string | null;
      ctaText?: string | null;
    }) =>
      request<Forme.Task>("POST", "/api/tasks", {
        name: task.name,
        description: task.description || "",
        type: TaskType.ME,
        productTopic: task.productTopic || null,
        callToAction: task.callToAction || null,
        estimatedCompletionTime: task.estimatedCompletionTime || null,
        productId: task.productId || null,
        productPhase: task.productPhase || null,
        templateId: task.templateId || null,
        completeWithAdvisor: task.completeWithAdvisor || false,
        downloadablePdfUrl: task.downloadablePdfUrl || null,
        triggerNotification: task.triggerNotification ?? false,
        ctaUrl: task.ctaUrl || null,
        ctaText: task.ctaText || null,
      }),
    {
      onMutate: async (newTask) => {
        await queryClient.cancelQueries({
          queryKey: ["taskList"],
        });
        const previousTasks = queryClient.getQueryData<Forme.EnrichedTask[]>([
          "taskList",
        ]);

        const taskToBeAdded = {
          ...newTask,
          id: random(100000).toString(),
          sortIndex: 1000000000,
          createdAt: moment().add(1, "day").format(),
          updatedAt: moment().add(1, "day").format(),
          taskStates: [
            {
              state: TaskStates.Added,
              id: random(100000).toString(),
              createdAt: moment().format(),
            },
          ],
        };

        queryClient.setQueryData<Forme.EnrichedTask[]>(
          ["taskList"],
          [
            taskToBeAdded as unknown as Forme.EnrichedTask,
            ...(previousTasks as Forme.EnrichedTask[]),
          ]
        );

        return { previousTasks };
      },
      onError: (err, taskToRemove, context) => {
        const typedContext: RemoveTaskContext =
          context as unknown as RemoveTaskContext;
        queryClient.setQueryData(["taskList"], typedContext.previousTasks);
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: ["taskList"],
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["getOpenTasksCount"],
        });
      },
    }
  );
};

const useUpdateDisabilityInsuranceInfo = (clientId: string) => {
  return useMutationFormeBackendApi<void, { data: DisabilityInsurancePayload }>(
    {
      method: "PUT",
      path: "/v1/clients/disability-insurance",
      params: { clientId },
    }
  );
};

const useUpdateTermLifeInsuranceInfo = (clientId: string) => {
  return useMutationFormeBackendApi<void, { data: TLPayload }>({
    method: "PUT",
    path: "/v1/clients/term-life-insurance",
    params: { clientId },
  });
};

const useUpdateProfileInfo = () => {
  return useMutationFormeBackendApi<void, { data: ProfilePayload }>({
    method: "PUT",
    path: "/v1/clients/profile",
  });
};

const useUpdateRiskToleranceInfo = (clientId: string) => {
  return useMutationFormeBackendApi<void, { data: RiskTolerancePayload }>({
    method: "PUT",
    path: "/v1/clients/risk-tolerance",
    params: { clientId },
  });
};

export {
  useDeleteTask,
  useMutateTaskState,
  useEditTask,
  useEditTaskAttachedNote,
  useCreateTask,
  useUpdateDisabilityInsuranceInfo,
  useUpdateTermLifeInsuranceInfo,
  useUpdateProfileInfo,
  useUpdateRiskToleranceInfo,
};
